<template>
  <div v-if="!prdEnv()">
    <!-- Header -->
    <header class="header">
      <div class="container">
        <nav class="navbar">
          <div class="brand-section">
            <img src="../../../static/image/logo_Getac.svg" style="height: 1.429rem;" alt="Getac Logo" />

            <div class="logo">
              FTIE TVO Agent
              <span class="status-indicator">{{ status }}</span>
            </div>
          </div>
          <ul class="nav-links">
            <li><a href="#" @click="navigateBack">Back to Models</a></li>
          </ul>
          <div class="mobile-menu-toggle" @click="toggleMobileMenu">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </nav>
        <!-- Mobile Menu -->
        <div v-if="mobileMenuOpen" class="mobile-menu">
          <ul class="mobile-nav-links">
            <li><a href="#" @click="navigateBack">Back to Models</a></li>
          </ul>
          </ul>
        </div>
      </div>
    </header>

    <!-- Chat Section -->
    <section id="chat" class="chat-section">
      <div class="container">
        <div class="chat-container">
          <!-- Chat Header -->
          <div class="chat-header">
            <div class="chat-header-content">
              <h2>Genova - FTIE AI Agent</h2>
              <p class="chat-subtitle">Powered by {{ selectedLLMProvider }}</p>
            </div>
            <div class="chat-status">
              <span class="status-dot running"></span>
              <span>Connected</span>
            </div>
          </div>

          <!-- Chat Messages -->
          <div class="chat-messages" ref="chatMessages">
            <div v-if="messages.length === 0" class="welcome-message">
              <div class="welcome-icon">💬</div>
              <h3>Welcome to FTIE TVO Agent</h3>
              <p>Start a conversation with our FTIE AI Agent. Input customer persona and customization request, I will generate a TVO (Total Value of Ownership) proposal for you!</p>
            </div>

            <div v-for="(message, index) in messages"
                 :key="index"
                 class="message-wrapper"
                 :class="message.role">
              <div class="message-content">
                <div class="message-avatar">
                  <span v-if="message.role === 'user'">👤</span>
                  <span v-else>🤖</span>
                </div>
                <div class="message-bubble">
                  <div class="message-text">{{ message.content }}</div>
                  <div class="message-time">{{ message.timestamp }}</div>
                </div>
              </div>
            </div>

            <div v-if="isTyping" class="message-wrapper assistant">
              <div class="message-content">
                <div class="message-avatar">🤖</div>
                <div class="message-bubble">
                  <div class="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Chat Input -->
          <div class="chat-input-container">
            <div class="chat-input-wrapper">
              <textarea v-model="userInput"
                        @keydown.enter.prevent="handleEnter"
                        placeholder="Type your message here..."
                        rows="1"
                        class="chat-input"
                        :disabled="isTyping"></textarea>
              <button @click="sendMessage()"
                      class="send-button"
                      :disabled="!userInput.trim() || isTyping">
                <span v-if="!isTyping">Send</span>
                <span v-else>...</span>
              </button>
            </div>
            <div class="chat-actions">
              <button @click="clearChat" class="action-btn outline">Clear Chat</button>
              <button @click="exportChat" class="action-btn outline">Export</button>
            </div>
          </div>
        </div>

        <!-- Chat Info Panel -->
        <div class="chat-info-panel">
          <div class="info-card">
            <h3>Model Information</h3>
            <div class="info-item">
              <span class="info-label">Model:</span>
              <span class="info-value">{{ selectedModel }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Status:</span>
              <span class="info-value status-running">Running</span>
            </div>
            <div class="info-item">
              <span class="info-label">Messages:</span>
              <span class="info-value">{{ messages.length }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Response Time:</span>
              <span class="info-value">{{ avgResponseTime }}sec</span>
            </div>
          </div>

          <div class="info-card">
            <h3>Quick Actions</h3>
            <button @click="sendSampleQuery('What are your capabilities?')" class="quick-action-btn">
              Ask about capabilities
            </button>
            <button @click="sendSampleQuery('Explain machine learning')" class="quick-action-btn">
              Ask about ML
            </button>
            <button @click="sendSampleQuery('Help me with code')" class="quick-action-btn">
              Code assistance
            </button>
            <button @click="sendSampleQuery('Summarize a document')" class="quick-action-btn">
              Document summary
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <p>&copy; 2025 Getac Advanced Intelligence Technologies Dept. </p>
      </div>
    </footer>
  </div>

  <div v-else class="coming-soon-page">
    <div class="coming-soon-card">

      <!-- Animated icon -->
      <div class="cs-icon-area">
        <div class="cs-icon-ring">
          <i class="fa fa-cogs cs-cog-icon"></i>
        </div>
        <div class="cs-steam-row">
          <span class="cs-steam"></span>
          <span class="cs-steam"></span>
          <span class="cs-steam"></span>
        </div>
      </div>

      <!-- Brand badge -->
      <div class="cs-brand-badge">
        <img src="https://www.getac.com/img/getac-logo.png"
             alt="Getac"
             class="cs-brand-logo"
             onerror="this.style.display='none'" />
        <span class="cs-brand-text">Getac On-Premise AI · Genova</span>
      </div>

      <!-- Heading -->
      <h1 class="cs-title">We're Cooking Something Great</h1>
      <p class="cs-subtitle">
        This feature is currently under active development and not yet available in this environment.<br />
        Our engineering team is working hard to bring you a powerful AI-enhanced experience.
      </p>

      <!-- Progress bar -->
      <div class="cs-progress-wrap">
        <div class="cs-progress-label d-flex justify-content-between mb-1">
          <span><i class="fa fa-code mr-1"></i>Development in progress</span>
          <span class="cs-progress-pct">In Dev</span>
        </div>
        <div class="cs-progress-track">
          <div class="cs-progress-bar"></div>
        </div>
      </div>

      <!-- Info chips -->
      <div class="cs-chips">
        <span class="cs-chip cs-chip-orange"><i class="fa fa-bolt mr-1"></i>AI Enhanced</span>
        <span class="cs-chip cs-chip-blue"><i class="fa fa-lock mr-1"></i>Pre-Production</span>
        <span class="cs-chip cs-chip-gray"><i class="fa fa-clock-o mr-1"></i>Launching Soon</span>
      </div>

    </div>
  </div>
</template>

<script>
  export default {
    name: 'CustomizationTvoAgent',
    data() {
      return {
        // BASE_URL: 'https://127.0.0.1:8000',
        status: 'Active',
        selectedLLMProvider: 'Ginstein (M365 Copilot Studio)',
        //selectedModel: 'Llama 2 13B Chat',
        messages: [],
        userInput: '',
        isTyping: false,
        mobileMenuOpen: false,
        avgResponseTime: 245,
        modelData: null,
        selectedModel: null,
        modelSize: null,
        modelParameterSize: null,
        modelFormat: null,
        modelFamily: null,
        quantizationLevel: null,
        modelStatus: null,
        searchByFtieId: null,
        searchedData: [],
        GRO_owner: '',
        GTC_owner: 'ALL',
        login_user_name: '',
        searching: false,
        pageDisabled: false,
        pageLimit: 10,
        perPage: 10,
        currentPage: 1,
        totalCount: '',
        closeSubTicketFTIE: null,
        closeSubTicketReason: '',
        keyword_category: 'Basic Info',
        es_context: '',
        input_to_copilot_studio_agent: '',
        customer: '',
        authUrl: '',
        frontendUrl: '',
        frontEndUrlToUse: this.$session.get('frontEndUrlToUse'),
        userInfo: '',
        userFunctions: [],
        userRoles: [],
        userSites: [],
        reason_text: null,
        already_first_search_flag: false,
        similarityResult: null,
        copilotStudioAgentResponse: null,
        printerForOutputTextAgent: '',
        paperAgent: '',
        paperOutputAgent: '',
        timer: null,

      }
    },
    mounted() {
      // Get model information from sessionStorage
      const storedModel = sessionStorage.getItem('selectedModel');
      if (storedModel) {
        const modelData = JSON.parse(storedModel);
        // this.modelId = modelData.modelId;
        this.selectedModel = modelData.model_name || 'Llama 2 13B Chat';
        this.modelSize = modelData.model_size || '';
        this.modelParameterSize = modelData.parameter_size || '';
        this.modelFormat = modelData.model_format || '';
        this.modelFamily = modelData.model_family || '';
        this.quantizationLevel = modelData.quantization_level || '';
        this.modelStatus = modelData.status || 'standby';
        this.modelStatus = this.modelStatus === 'running' ? 'Active' : 'Standby';
        // Optional: Store model data for later use
        this.modelData = modelData;
      }
      this.scrollToBottom();
    },
    methods: {
      prdEnv() {
        return 'PRD' === this.$session.get('mode');
      },
      callCopilotStudioAgent(content) {
        let self = this;
        let resultText = null;
        let searchedData = null;
        let totalCount = 0;
        // let url_NOVACORE_EMBEDDING = self.$root.baseUrl + '/ai-service/similarity/';
        let url_python_ora23VDB_EMBEDDING = self.$root.baseUrl + '/ai-service/copilot-studio-agent/';
        let url = url_python_ora23VDB_EMBEDDING;
        var model_selected = [];
        if (self.model.length !== 0) {
          for (i = 0; i < self.model.length; i++) {
            model_selected.push(self.model[i]['value'])
          }
        }
        let request_data = {
          ftie_id: '',
          context: content,
          sfdc_request_type: '',
          es_description: content,
          es_issue_req: content,
          device: model_selected,
          site: self.GRO,
          vertical: ''
        };

        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };
        eventHub.$emit('loading', true);
        self.$axios.post(url, request_data, { params })
          .then((response) => {
            var res = response.data;
            //console.log(res);
            if (response.status === 200) {
              resultText = res['agent_result'];
            }
            else {
              // self.$message.exception('Internal Server Error!');
              resultText = null
            }
          }, (error) => {
            console.log(error);
            self.$message.exception('Internal Server Error!');
          })
          .finally(() => {

            self.searching = false;
            self.already_first_search_flag = true;
            if (self.already_first_search_flag == true) {
              eventHub.$emit('loading', false);
            }
            // self.searchedData = searchedData;
            // self.totalCount = totalCount;
            self.copilotStudioAgentResponse = resultText;
            self.printerForOutputTextAgent = resultText;
          })

      },

      navigateBack() {
        console.log('Navigate back to models page');
        window.history.back();
      },
      toggleMobileMenu() {
        this.mobileMenuOpen = !this.mobileMenuOpen;
      },
      handleEnter(event) {
        if (!event.shiftKey) {
          this.sendMessage();
        }
      },
      sendMessage() {
        if (!this.userInput.trim() || this.isTyping) return;

        const userMessage = {
          role: 'user',
          content: this.userInput,
          timestamp: this.getCurrentTime()
        };

        this.messages.push(userMessage);
        const query = this.userInput;
        this.userInput = '';

        this.$nextTick(() => {
          this.scrollToBottom();
        });

        // Simulate AI response
        this.getAIResponse(query);
      },
      getAIResponse(query) {
        let self = this;
        let aiResponse = '';
        self.isTyping = true;

        // Simulate API call to your backend
        let request_data = {
          whoId: 'alice',
          llm_model: self.modelData,
          content: query,
          parameters: []
        };
        // self.$axios.post(self.BASE_URL + '/novacore/generate/', request_data)
        self.$axios.post(self.$root.baseUrl + '/ai-service/copilot-studio-agent/', request_data)
          .then((response) => {
            var res = response.data;
            if (response.status === 200) {
              // self.avgResponseTime = res['elapsed_time'];
              aiResponse = res['agent_result']; //JSON.parse(res['ai_response']);
              const aiMessage = {
                role: 'assistant',
                content: aiResponse,
                timestamp: self.getCurrentTime()
              };
              self.messages.push(aiMessage);
            }
          })
          .catch((error) => {
            console.error(error);
            const errorMessage = {
              role: 'assistant',
              content: 'Sorry, I encountered an error. Please try again.',
              timestamp: self.getCurrentTime()
            };
            self.messages.push(errorMessage);
          })
          .finally(() => {
            self.isTyping = false;
            self.$nextTick(() => {
              self.scrollToBottom();
            });
          });


        // For demonstration, using setTimeout to simulate API response
        /*
        setTimeout(() => {
          const aiMessage = {
            role: 'assistant',
            content: `I received your message: "${query}". This is a simulated response. In production, this would connect to your NovaCore backend at ${self.BASE_URL}/novacore/chat/.`,
            timestamp: self.getCurrentTime()
          };
          self.messages.push(aiMessage);
          self.isTyping = false;

          self.$nextTick(() => {
            self.scrollToBottom();
          });
        }, 150000); */
      },
      sendSampleQuery(query) {
        this.userInput = query;
        this.sendMessage();
      },
      clearChat() {
        if (confirm('Are you sure you want to clear the chat history?')) {
          this.messages = [];
        }
      },
      exportChat() {
        const chatData = JSON.stringify(this.messages, null, 2);
        const blob = new Blob([chatData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chat-export-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
      },
      getCurrentTime() {
        const now = new Date();
        return now.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        });
      },
      scrollToBottom() {
        const container = this.$refs.chatMessages;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }
    }
  }
</script>

<style scoped>

  /* ========================================
COMING SOON PAGE
======================================== */
  .coming-soon-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #fdf6f0 0%, #f0f4f8 100%);
    padding: 2rem 1rem;
  }

  .coming-soon-card {
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.10);
    padding: 3rem 2.5rem 2.5rem;
    max-width: 520px;
    width: 100%;
    text-align: center;
    border-top: 4px solid #f77530;
  }

  /* --- Icon area --- */
  .cs-icon-area {
    margin-bottom: 1.2rem;
  }

  .cs-icon-ring {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #fff3ec, #ffe5d0);
    border: 2px solid rgba(247, 117, 48, 0.25);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.4rem;
  }

  .cs-cog-icon {
    font-size: 2.2rem;
    color: #f77530;
    animation: cs-spin 6s linear infinite;
  }

  @keyframes cs-spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  .cs-steam-row {
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  .cs-steam {
    display: inline-block;
    width: 5px;
    height: 16px;
    background: #f77530;
    border-radius: 4px;
    opacity: 0;
    animation: cs-steam-rise 1.8s ease-in-out infinite;
  }

    .cs-steam:nth-child(2) {
      animation-delay: 0.35s;
    }

    .cs-steam:nth-child(3) {
      animation-delay: 0.7s;
    }

  @keyframes cs-steam-rise {
    0% {
      transform: translateY(0);
      opacity: 0;
    }

    40% {
      transform: translateY(-8px);
      opacity: 0.5;
    }

    100% {
      transform: translateY(-18px);
      opacity: 0;
    }
  }

  /* --- Brand badge --- */
  .cs-brand-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 999px;
    padding: 0.3rem 0.9rem;
    margin-bottom: 1.2rem;
  }

  .cs-brand-logo {
    height: 16px;
    object-fit: contain;
  }

  .cs-brand-text {
    font-size: 0.72rem;
    font-weight: 600;
    color: #6c757d;
    letter-spacing: 0.03em;
  }

  /* --- Text --- */
  .cs-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 0.75rem;
  }

  .cs-subtitle {
    font-size: 0.88rem;
    color: #718096;
    line-height: 1.7;
    margin-bottom: 1.6rem;
  }

  /* --- Progress bar --- */
  .cs-progress-wrap {
    margin-bottom: 1.6rem;
    text-align: left;
  }

  .cs-progress-label {
    font-size: 0.75rem;
    color: #888;
    font-weight: 600;
  }

  .cs-progress-pct {
    color: #f77530;
    font-weight: 700;
  }

  .cs-progress-track {
    height: 8px;
    background: #f0f0f0;
    border-radius: 999px;
    overflow: hidden;
  }

  .cs-progress-bar {
    height: 100%;
    width: 65%;
    background: linear-gradient(90deg, #f77530, #e05a10);
    border-radius: 999px;
    animation: cs-progress-pulse 3s ease-in-out infinite;
  }

  /*  ======================================== */

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    line-height: 1.6;
    color: #333;
  }

  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 20px;
  }

  /* Header */
  .header {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    padding: 1rem 0;
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
  }

  .navbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .brand-section {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .company-logo {
    height: 1.429rem;
  }

  .logo {
    display: flex;
    align-items: center;
    font-size: 1.8rem;
    font-weight: bold;
    color: #2c3e50;
  }

  .status-indicator {
    display: inline-block;
    padding: 5px 10px;
    background: linear-gradient(45deg, #28a745, #20c997);
    color: white;
    border-radius: 15px;
    font-size: 0.8rem;
    font-weight: 600;
    margin-left: 10px;
  }

  .nav-links {
    display: flex;
    list-style: none;
    gap: 2rem;
  }

    .nav-links a {
      text-decoration: none;
      color: #555;
      font-weight: 500;
      transition: color 0.3s ease;
      cursor: pointer;
    }

      .nav-links a:hover {
        color: #667eea;
      }

  .mobile-menu-toggle {
    display: none;
  }

  /* Chat Section */
  .chat-section {
    padding: 6.5rem 2rem 2rem;
    min-height: 100vh;
    /*background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  }

    .chat-section .container {
      display: grid;
      grid-template-columns: 1fr 350px;
      gap: 2rem;
      max-width: 1400px;
    }

  .chat-container {
    background: white;
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    height: calc(100vh - 12rem);
    overflow: hidden;
  }

  .chat-header {
    background: linear-gradient(45deg, #667eea, #764ba2);
    color: white;
    padding: 1.5rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .chat-header-content h2 {
    margin: 0;
    font-size: 1.5rem;
  }

  .chat-subtitle {
    font-size: 0.9rem;
    opacity: 0.9;
    margin-top: 0.25rem;
  }

  .chat-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(255, 255, 255, 0.2);
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
  }

    .status-dot.running {
      background: #28a745;
      animation: pulse 2s infinite;
    }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }

    50% {
      opacity: 0.5;
    }
  }

  /* Chat Messages */
  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 2rem;
    background: #f8f9fa;
  }

  .welcome-message {
    text-align: center;
    padding: 4rem 2rem;
    color: #666;
  }

  .welcome-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }

  .welcome-message h3 {
    color: #2c3e50;
    margin-bottom: 0.5rem;
  }

  .message-wrapper {
    margin-bottom: 1.5rem;
    display: flex;
  }

    .message-wrapper.user {
      justify-content: flex-end;
    }

    .message-wrapper.assistant {
      justify-content: flex-start;
    }

  .message-content {
    display: flex;
    gap: 0.75rem;
    max-width: 70%;
  }

  .message-wrapper.user .message-content {
    flex-direction: row-reverse;
  }

  .message-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(45deg, #667eea, #764ba2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  .message-wrapper.user .message-avatar {
    background: linear-gradient(45deg, #28a745, #20c997);
  }

  .message-bubble {
    background: white;
    padding: 1rem 1.25rem;
    border-radius: 15px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }

  .message-wrapper.user .message-bubble {
    background: linear-gradient(45deg, #667eea, #764ba2);
    color: white;
  }

  .message-text {
    line-height: 1.6;
    word-wrap: break-word;
  }

  .message-time {
    font-size: 0.75rem;
    margin-top: 0.5rem;
    opacity: 0.7;
  }

  /* Typing Indicator */
  .typing-indicator {
    display: flex;
    gap: 0.25rem;
    padding: 0.5rem 0;
  }

    .typing-indicator span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #667eea;
      animation: typing 1.4s infinite;
    }

      .typing-indicator span:nth-child(2) {
        animation-delay: 0.2s;
      }

      .typing-indicator span:nth-child(3) {
        animation-delay: 0.4s;
      }

  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
    }

    30% {
      transform: translateY(-10px);
    }
  }

  /* Chat Input */
  .chat-input-container {
    padding: 1.5rem 2rem;
    background: white;
    border-top: 1px solid #e9ecef;
  }

  .chat-input-wrapper {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .chat-input {
    flex: 1;
    padding: 1rem;
    border: 2px solid #e9ecef;
    border-radius: 10px;
    font-family: inherit;
    font-size: 1rem;
    resize: none;
    transition: border-color 0.3s ease;
  }

    .chat-input:focus {
      outline: none;
      border-color: #667eea;
    }

    .chat-input:disabled {
      background: #f8f9fa;
      cursor: not-allowed;
    }

  .send-button {
    padding: 1rem 2rem;
    background: linear-gradient(45deg, #667eea, #764ba2);
    color: white;
    border: none;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
  }

    .send-button:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
    }

    .send-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

  .chat-actions {
    display: flex;
    gap: 0.5rem;
  }

  .action-btn {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.9rem;
  }

    .action-btn.outline {
      background: transparent;
      border: 2px solid #667eea;
      color: #667eea;
    }

      .action-btn.outline:hover {
        background: #667eea;
        color: white;
      }

  /* Info Panel */
  .chat-info-panel {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .info-card {
    background: white;
    padding: 1.5rem;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

    .info-card h3 {
      color: #2c3e50;
      margin-bottom: 1rem;
      font-size: 1.2rem;
    }

  .info-item {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0;
    border-bottom: 1px solid #e9ecef;
  }

    .info-item:last-child {
      border-bottom: none;
    }

  .info-label {
    color: #666;
    font-weight: 500;
  }

  .info-value {
    color: #2c3e50;
    font-weight: 600;
  }

  .status-running {
    color: #28a745;
  }

  .quick-action-btn {
    width: 100%;
    padding: 0.75rem;
    margin-bottom: 0.5rem;
    background: linear-gradient(45deg, #667eea, #764ba2);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
  }

    .quick-action-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
    }

  /* Footer */
  .footer {
    background: #2c3e50;
    color: white;
    text-align: center;
    padding: 2rem 0;
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .chat-section .container {
      grid-template-columns: 1fr;
    }

    .chat-info-panel {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 768px) {
    .chat-section {
      padding: 6rem 1rem 1rem;
    }

    .chat-container {
      height: calc(100vh - 10rem);
    }

    .message-content {
      max-width: 85%;
    }

    .chat-info-panel {
      grid-template-columns: 1fr;
    }

    .brand-section {
      flex-direction: column;
      gap: 0.5rem;
    }

    .company-logo {
      height: 1.2rem;
    }

    .logo {
      font-size: 1.4rem;
    }

    .nav-links {
      display: none;
    }

    .mobile-menu-toggle {
      display: flex;
      flex-direction: column;
      gap: 4px;
      cursor: pointer;
    }

      .mobile-menu-toggle span {
        width: 25px;
        height: 3px;
        background: #2c3e50;
        border-radius: 3px;
      }

    .mobile-menu {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: white;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    }

    .mobile-nav-links {
      list-style: none;
      padding: 1rem;
    }

      .mobile-nav-links li {
        padding: 0.75rem;
      }

      .mobile-nav-links a {
        text-decoration: none;
        color: #555;
        font-weight: 500;
      }
  }
</style>



// WEBPACK FOOTER //
// src/components/framework-gen-ai/FTIETVOAgent.vue